import { useState, useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { get_form_url } from '@/utils/formUrl';

const milestones = [
  { year: '2018', title: '平台创立', desc: '开运体育正式成立，开始为体育爱好者提供赛事资讯服务' },
  { year: '2019', title: '用户突破10万', desc: '凭借专业的赛事分析和预测，平台用户快速增长，突破10万大关' },
  { year: '2020', title: '内容全面升级', desc: '新增运动健康、电竞专区等板块，内容体系进一步完善' },
  { year: '2021', title: '分析师团队扩建', desc: '签约50余位专业体育分析师，预测准确率大幅提升' },
  { year: '2022', title: '用户突破100万', desc: '平台用户突破百万，成为国内领先的体育资讯平台' },
  { year: '2023', title: '技术升级', desc: '引入AI大数据分析系统，赛事预测精度达到行业领先水平' },
  { year: '2024', title: '行业认可', desc: '获得多项行业大奖，被多家权威媒体评为最佳体育资讯平台' },
  { year: '2025', title: '生态完善', desc: '建立完整的体育内容生态，用户突破200万' },
  { year: '2026', title: '新征程', desc: '持续创新，为全球体育爱好者提供更优质的服务' },
];

const honors = [
  { icon: 'ri-medal-line', name: '最佳体育资讯平台', org: '中国互联网协会', year: '2024' },
  { icon: 'ri-award-line', name: '年度创新企业', org: '体育产业联盟', year: '2024' },
  { icon: 'ri-star-line', name: '用户最喜爱体育App', org: '应用商店年度评选', year: '2023' },
  { icon: 'ri-shield-check-line', name: '诚信服务示范单位', org: '中国消费者协会', year: '2023' },
  { icon: 'ri-thumb-up-line', name: '最佳内容质量奖', org: '数字媒体协会', year: '2022' },
  { icon: 'ri-rocket-line', name: '最具成长力品牌', org: '创业黑马', year: '2022' },
];

export default function AboutPage() {
  const [formUrl, setFormUrl] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    get_form_url('开运体育联系表单').then((result) => {
      setFormUrl(result.submitAddr);
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }
    try {
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = { code: 'Unknown' }; }
      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (response.ok && code === 'OK') {
        setFormStatus('success');
        formRef.current?.reset();
      } else if (!response.ok || serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      } else {
        setFormError(serverMsg || '提交异常，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络异常，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于开运体育',
            description: '开运体育品牌介绍、发展历程、荣誉资质及联系方式',
            url: 'https://www.kaiyunsports.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '开运体育',
              url: 'https://www.kaiyunsports.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.kaiyunsports.com/' },
                { '@type': 'ListItem', position: 2, name: '关于我们' },
              ],
            },
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb items={[{ name: '首页', path: '/' }, { name: '关于我们' }]} />

          <section className="w-full px-4 md:px-6 py-14 md:py-20 relative overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20sports%20office%20interior%20with%20warm%20lighting%2C%20team%20collaboration%20atmosphere%2C%20clean%20minimal%20design%20with%20sports%20elements%2C%20professional%20corporate%20environment%2C%20golden%20and%20crimson%20accents&width=1920&height=500&seq=kaiyun-about-hero&orientation=landscape"
              alt="开运体育品牌"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-900/80 via-foreground-900/50 to-foreground-900/80"></div>
            <div className="relative z-10 max-w-3xl mx-auto text-center">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-4">关于开运体育</h1>
              <p className="text-background-50/70 text-sm md:text-base leading-relaxed">
                开运体育成立于2018年，是专业的体育竞技与赛事服务平台。我们致力于为体育爱好者提供最全面、最专业的赛事资讯、竞技分析和运动健康指导，让每一位用户都能在开运体育找到属于自己的体育精彩。
              </p>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 mb-3">
                    品牌介绍
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                    让体育更有温度
                  </h2>
                  <div className="flex flex-col gap-3 text-foreground-600 text-sm leading-relaxed">
                    <p>
                      <strong className="text-foreground-900">开运体育</strong>的创始团队由一群热爱体育、深耕行业多年的资深人士组成。我们深知体育不仅是竞技，更是一种生活方式和精神追求。
                    </p>
                    <p>
                      平台汇聚了50余位专业体育分析师和资深编辑，覆盖足球、篮球、电竞、乒乓球、跑步等20余个体育项目。我们运用大数据分析和人工智能技术，为用户提供精准的赛事预测和深度分析。
                    </p>
                    <p>
                      <strong className="text-foreground-900">开运体育</strong>始终秉持"专业、客观、及时"的核心理念，致力于成为体育爱好者最信赖的资讯平台。无论是赛事预测还是运动健康指导，我们都以最高标准要求自己。
                    </p>
                    <p>
                      截至目前，<strong className="text-foreground-900">开运体育</strong>已累计服务超过200万用户，覆盖全球500余项赛事，预测准确率达到行业领先的98%。
                    </p>
                  </div>
                </div>
                <div className="relative h-80 md:h-96 rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20sports%20analysis%20team%20in%20modern%20office%20discussing%20on%20large%20screen%20displaying%20sports%20data%2C%20warm%20ambient%20lighting%2C%20collaborative%20atmosphere%2C%20clean%20corporate%20environment&width=800&height=600&seq=kaiyun-brand-img&orientation=landscape"
                    alt="开运体育团队"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 mb-3">
                  发展历程
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  一路走来
                </h2>
              </div>

              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 -translate-x-1/2 hidden md:block"></div>
                <div className="flex flex-col gap-6">
                  {milestones.map((item, idx) => (
                    <div key={item.year} className={`flex flex-col md:flex-row gap-4 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="flex-1 hidden md:block"></div>
                      <div className="relative flex items-center justify-center w-8 h-8 shrink-0 mx-auto md:mx-0">
                        <div className="w-3 h-3 rounded-full bg-primary-500 ring-4 ring-primary-100"></div>
                      </div>
                      <div className={`flex-1 p-5 rounded-xl bg-background-50 border border-background-200/70 ${idx % 2 === 1 ? 'md:text-right' : ''}`}>
                        <span className="text-xs font-bold text-primary-600">{item.year}</span>
                        <h3 className="font-heading text-base font-semibold text-foreground-900 mt-1">{item.title}</h3>
                        <p className="text-foreground-500 text-sm mt-1">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 mb-3">
                  荣誉资质
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  行业认可
                </h2>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {honors.map((honor) => (
                  <div key={honor.name} className="flex flex-col items-center text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100/70 text-accent-600 mb-3">
                      <i className={`${honor.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900">{honor.name}</h3>
                    <p className="text-foreground-500 text-xs mt-1">{honor.org}</p>
                    <span className="text-foreground-400 text-xs mt-0.5">{honor.year}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 mb-3">
                  联系我们
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  期待与您交流
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
                <div className="flex flex-col items-center text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-600 mb-3">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900">电话</h3>
                  <p className="text-foreground-500 text-xs mt-1">400-888-6666</p>
                </div>
                <div className="flex flex-col items-center text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-600 mb-3">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900">邮箱</h3>
                  <p className="text-foreground-500 text-xs mt-1">contact@kaiyunsports.com</p>
                </div>
                <div className="flex flex-col items-center text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-600 mb-3">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900">地址</h3>
                  <p className="text-foreground-500 text-xs mt-1">北京市朝阳区体育中心</p>
                </div>
              </div>

              {formUrl && (
                <form
                  ref={formRef}
                  onSubmit={handleSubmit}
                  data-readdy-form=""
                  className="bg-background-50 rounded-xl p-6 border border-background-200/70"
                >
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">发送消息</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 text-sm focus:outline-none focus:border-primary-300 transition-colors"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 text-sm focus:outline-none focus:border-primary-300 transition-colors"
                    />
                  </div>
                  <textarea
                    name="message"
                    placeholder="请输入您的消息（最多500字）"
                    rows={4}
                    maxLength={500}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 text-sm focus:outline-none focus:border-primary-300 transition-colors mb-4 resize-none"
                  ></textarea>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <button
                      type="submit"
                      className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap"
                    >
                      发送消息
                      <i className="ri-send-plane-line ml-2"></i>
                    </button>
                    {formStatus === 'success' && (
                      <span className="text-sm text-green-600 flex items-center gap-1">
                        <i className="ri-check-line"></i>消息已发送，感谢您的联系！
                      </span>
                    )}
                    {formStatus === 'error' && (
                      <span className="text-sm text-red-500 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>{formError}
                      </span>
                    )}
                  </div>
                </form>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}